import { Beacon } from '@/components/forms/FormContext';

export type FlowConfig<T extends string> = {
	[key in T]: { gotoStep: T | null };
};

export const getSplitFormGraph = <StepMap extends string>(
	config: FlowConfig<StepMap>,
	steps: Record<string, StepMap>,
): Beacon<StepMap> => {
	const graph: Beacon<StepMap> = {};

	Object.keys(config).forEach((step) => {
		const currentStepValue = steps[step];
		const nextStep = config[step as StepMap].gotoStep;

		if (nextStep) {
			graph[currentStepValue] = [steps[nextStep]];
		} else {
			graph[currentStepValue] = [];
		}
	});

	return graph;
};

export const getSplitInitialStep = <StepMap extends string>(
	config: FlowConfig<StepMap>,
): StepMap => {
	const steps = Object.keys(config);
	if (steps.length === 1) {
		return steps[0] as StepMap;
	}

	const destinations = new Set(
		Object.values(config as Record<string, { gotoStep: StepMap | null }>)
			.map(({ gotoStep }) => gotoStep)
			.filter(Boolean),
	);

	const startingSteps = steps.filter(
		(step) =>
			!destinations.has(step as StepMap) &&
			config[step as StepMap].gotoStep !== null,
	);

	if (startingSteps.length !== 1) {
		throw new Error(
			'No valid starting step found - flow may have circular dependencies',
		);
	}

	return startingSteps[0] as StepMap;
};

export function getSplitNextStep<StepMap extends string>(
	currentStep: StepMap,
	config?: FlowConfig<StepMap>,
): StepMap | null {
	if (!config) {
		return null;
	}

	const nextStep = config[currentStep]?.gotoStep;

	if (!nextStep) {
		return null;
	}

	return nextStep;
}
